/**
 * Single owner of the in-call audio *desired* route.
 *
 * Native iOS/Android (and WebRTC) re-route to the loud speaker at many moments
 * (getUserMedia, startAudioInput, CallKit didActivate, Telecom callbacks,
 * Bluetooth disconnect, connecting-tone MediaPlayer, foreground return). Pinning
 * the earpiece at one timestamp is not enough — every later platform event must
 * re-assert THIS arbiter's target. Hardware snapshots are never recorded as a
 * user toggle.
 *
 * Authority (highest wins):
 *  1. Explicit user selection for THIS call (earpiece / speaker / named device)
 *  2. Connected car / Bluetooth / wired (never yanked to a builtin default)
 *  3. Phase/media default: earpiece (native voice) / speaker (video, ringtone, web)
 *
 * Oscillation is impossible: a correction writes an echo window. Route-change
 * events the arbiter itself caused, and reversals inside the cooldown, are
 * ignored. A route-change storm converges on one target instead of ping-ponging.
 */

export type CallAudioPhase =
  | 'idle'
  | 'outgoing-ringback'
  | 'incoming-ringtone'
  | 'connecting'
  | 'connected'

export type CallAudioMediaType = 'voice' | 'video'

export type HardwareAudioRoute = 'earpiece' | 'speaker' | 'bluetooth' | 'wired' | 'car'

/** Builtin-or-external target a correction may request. */
export type RouteTarget = 'earpiece' | 'speaker' | 'external'

export type UserAudioOutput = 'earpiece' | 'speaker' | 'external'

export type RouteCorrection = {
  action: 'keep' | 'correct' | 'ignore'
  from: HardwareAudioRoute
  to: RouteTarget
  phase: CallAudioPhase
  trigger: string
  reason: string
  desiredSpeakerOn: boolean
  userToggled: boolean
}

export type CallAudioArbiterSnapshot = {
  phase: CallAudioPhase
  mediaType: CallAudioMediaType | null
  userSpeakerOn: boolean | null
  userOutput: UserAudioOutput | null
  desiredSpeakerOn: boolean
}

export type ObserveHardwareOptions = {
  /** Epoch ms. Tests inject this so cooldown is deterministic. */
  now?: number
  /**
   * True when a car / Bluetooth / wired communication device is AVAILABLE
   * (not merely paired). A speaker blip while an external device is still
   * attached must restore the external route, never force earpiece.
   */
  availableExternal?: boolean
}

/** Minimum gap between two corrections. Shorter storms collapse to one apply. */
export const ROUTE_CORRECTION_MIN_INTERVAL_MS = 800
/** After a correction, ignore hardware events that disagree with the target. */
export const ROUTE_CORRECTION_ECHO_MS = 1_200

export class CallAudioRouteArbiter {
  private phase: CallAudioPhase = 'idle'
  private mediaType: CallAudioMediaType | null = null
  /** Explicit in-call toggle. Null = use the phase/media default. Never set from hardware. */
  private userSpeakerOn: boolean | null = null
  /** Finer-grained user pick. Null = no explicit pick this call. */
  private userOutput: UserAudioOutput | null = null

  private lastCorrectionAt = 0
  /** When the user last picked an output in-app (0 = never this call). */
  private userSelectedAt = 0
  private lastCorrectedTo: RouteTarget | null = null
  private echoUntil = 0
  private correctionsIssued = 0

  constructor(
    private isNativeApp: boolean,
    private readonly nowFn: () => number = () => Date.now(),
  ) {}

  /** Capacitor userAgent is unreliable on Android WebView — set from isNativePlatform(). */
  setNative(isNative: boolean): void {
    this.isNativeApp = isNative
  }

  begin(mediaType: CallAudioMediaType, phase: CallAudioPhase): void {
    this.mediaType = mediaType
    this.phase = phase === 'idle' ? 'connecting' : phase
    this.userSpeakerOn = null
    this.userOutput = null
    this.userSelectedAt = 0
    this.lastCorrectionAt = 0
    this.lastCorrectedTo = null
    this.echoUntil = 0
    this.correctionsIssued = 0
  }

  setPhase(phase: CallAudioPhase): void {
    if (this.phase === 'idle' && phase === 'idle') return
    // A late incoming-ringtone start must not flip a live call back to speaker.
    if (
      phase === 'incoming-ringtone' &&
      (this.phase === 'outgoing-ringback' ||
        this.phase === 'connecting' ||
        this.phase === 'connected')
    ) {
      return
    }
    this.phase = phase
  }

  setMediaType(mediaType: CallAudioMediaType): void {
    this.mediaType = mediaType
  }

  /** User tapped the in-call speaker control. This is the only way hardware is "owned". */
  userToggle(speakerOn: boolean): { desiredSpeakerOn: boolean } {
    this.userSpeakerOn = speakerOn
    this.userOutput = speakerOn ? 'speaker' : 'earpiece'
    this.userSelectedAt = this.nowFn()
    if (this.phase === 'idle') this.phase = 'connected'
    this.clearEcho()
    return { desiredSpeakerOn: this.desiredSpeakerOn() }
  }

  /**
   * User picked a specific output from the route picker. `external` means
   * "whatever car / Bluetooth / wired device is connected".
   */
  userSelect(output: UserAudioOutput): { desiredSpeakerOn: boolean } {
    this.userOutput = output
    this.userSpeakerOn = output === 'speaker' ? true : output === 'earpiece' ? false : null
    this.userSelectedAt = this.nowFn()
    if (this.phase === 'idle') this.phase = 'connected'
    this.clearEcho()
    return { desiredSpeakerOn: this.desiredSpeakerOn() }
  }

  /** Drop the explicit toggle so the phase/media default applies again. */
  clearUserToggle(): void {
    this.userSpeakerOn = null
    this.userOutput = null
  }

  desiredSpeakerOn(): boolean {
    if (this.userOutput === 'speaker') return true
    if (this.userOutput === 'earpiece') return false
    if (this.userOutput === 'external') return false
    if (this.userSpeakerOn !== null) return this.userSpeakerOn
    return this.defaultDesired()
  }

  isActive(): boolean {
    return this.phase !== 'idle'
  }

  /**
   * The platform moved the call to Bluetooth / wired (Android Auto, system
   * output switcher, headset connect). Supersedes an earlier in-app builtin
   * pick — unless it lands inside the echo window of that pick (a stale
   * route event racing the user's own choice). Returns true when applied.
   */
  observeSystemExternalSelect(echoWindowMs = ROUTE_CORRECTION_ECHO_MS): boolean {
    if (this.userOutput !== 'earpiece' && this.userOutput !== 'speaker') return false
    if (this.userSelectedAt > 0 && this.nowFn() - this.userSelectedAt < echoWindowMs) return false
    this.userOutput = 'external'
    this.userSpeakerOn = null
    this.clearEcho()
    return true
  }

  hasUserToggle(): boolean {
    return this.userSpeakerOn !== null || this.userOutput !== null
  }

  snapshot(): CallAudioArbiterSnapshot {
    return {
      phase: this.phase,
      mediaType: this.mediaType,
      userSpeakerOn: this.userSpeakerOn,
      userOutput: this.userOutput,
      desiredSpeakerOn: this.desiredSpeakerOn(),
    }
  }

  /** Test/telemetry: how many corrections this call has issued. */
  correctionCount(): number {
    return this.correctionsIssued
  }

  /**
   * A platform route-change event. Never mutates `userSpeakerOn`. Returns
   * whether the live built-in route must be corrected back to the desired one.
   *
   * Oscillation invariant: given a storm of alternating built-in route events
   * with no user toggle and no external attach/detach, at most one `correct`
   * is issued per echo window; subsequent events are `ignore` or `keep`.
   */
  observeHardware(
    route: HardwareAudioRoute,
    trigger: string,
    opts: ObserveHardwareOptions = {},
  ): RouteCorrection {
    const now = opts.now ?? this.nowFn()
    const desired = this.desiredSpeakerOn()
    const phase = this.phase
    const userToggled = this.hasUserToggle()
    const availableExternal = opts.availableExternal === true
    const isExternal = route === 'bluetooth' || route === 'wired' || route === 'car'

    const keep = (
      to: RouteTarget,
      reason: string,
      action: 'keep' | 'ignore' = 'keep',
    ): RouteCorrection => ({
      action,
      from: route,
      to,
      phase,
      trigger,
      reason,
      desiredSpeakerOn: desired,
      userToggled,
    })

    // Echo of our own setCommunicationDevice / overrideOutputAudioPort: the
    // platform often reports the OLD or a transient speaker route immediately
    // after we applied the target. Swallow it instead of reversing.
    if (now < this.echoUntil && this.lastCorrectedTo) {
      if (this.hardwareMatchesTarget(route, this.lastCorrectedTo) || isExternal) {
        return keep(this.lastCorrectedTo, 'echo-converged')
      }
      return keep(this.lastCorrectedTo, 'echo-suppress', 'ignore')
    }

    // User explicitly picked a builtin. Only then may we yank off Bluetooth/car.
    if (isExternal) {
      if (this.userOutput === 'speaker') {
        return this.issueCorrect(route, 'speaker', trigger, now, 'enforce-user-toggle')
      }
      if (this.userOutput === 'earpiece') {
        return this.issueCorrect(route, 'earpiece', trigger, now, 'enforce-user-toggle')
      }
      return keep('external', 'external-precedence')
    }

    // External device is still attached; current built-in reading is a blip
    // (connecting tone, WebRTC, Telecom). Restore/keep external — never earpiece.
    if (availableExternal && this.userOutput !== 'speaker' && this.userOutput !== 'earpiece') {
      return keep('external', 'external-available')
    }

    const want: 'earpiece' | 'speaker' = desired ? 'speaker' : 'earpiece'
    if (route === want) {
      return keep(want, 'already-desired')
    }

    // Cooldown: a second correction inside the interval cannot reverse the first.
    if (this.lastCorrectedTo && now - this.lastCorrectionAt < ROUTE_CORRECTION_MIN_INTERVAL_MS) {
      if (this.lastCorrectedTo === want) {
        return keep(want, 'cooldown-same-target', 'ignore')
      }
      return keep(this.lastCorrectedTo, 'cooldown-no-reverse', 'ignore')
    }

    return this.issueCorrect(
      route,
      want,
      trigger,
      now,
      userToggled ? 'enforce-user-toggle' : 'enforce-default',
    )
  }

  reset(): void {
    this.phase = 'idle'
    this.mediaType = null
    this.userSpeakerOn = null
    this.userOutput = null
    this.clearEcho()
    this.correctionsIssued = 0
  }

  private issueCorrect(
    from: HardwareAudioRoute,
    to: RouteTarget,
    trigger: string,
    now: number,
    reason: string,
  ): RouteCorrection {
    this.lastCorrectionAt = now
    this.lastCorrectedTo = to
    this.echoUntil = now + ROUTE_CORRECTION_ECHO_MS
    this.correctionsIssued += 1
    return {
      action: 'correct',
      from,
      to,
      phase: this.phase,
      trigger,
      reason,
      desiredSpeakerOn: this.desiredSpeakerOn(),
      userToggled: this.hasUserToggle(),
    }
  }

  private hardwareMatchesTarget(route: HardwareAudioRoute, target: RouteTarget): boolean {
    if (target === 'external') return route === 'bluetooth' || route === 'wired' || route === 'car'
    return route === target
  }

  private clearEcho(): void {
    this.lastCorrectionAt = 0
    this.lastCorrectedTo = null
    this.echoUntil = 0
  }

  private defaultDesired(): boolean {
    // Incoming ringtone must be audible across the room even before mediaType
    // is stamped. Ringback / connecting / connected media on a native voice
    // call are held to the ear.
    if (this.phase === 'incoming-ringtone') return true
    if (this.phase === 'idle' || !this.mediaType) return false
    if (this.mediaType === 'video') return true
    if (this.isNativeApp && this.mediaType === 'voice') return false
    return true
  }
}

function detectNativeApp(): boolean {
  try {
    const cap = (globalThis as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor
    if (cap && typeof cap.isNativePlatform === 'function') return cap.isNativePlatform()
  } catch {
    /* tests / SSR */
  }
  return (
    typeof navigator !== 'undefined' &&
    typeof (navigator as { userAgent?: string }).userAgent === 'string' &&
    /Capacitor/i.test((navigator as { userAgent: string }).userAgent)
  )
}

/** Process-wide arbiter. Tests should construct {@link CallAudioRouteArbiter} directly. */
export const callAudioRouteArbiter = new CallAudioRouteArbiter(detectNativeApp())
